import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useClickOutside } from "@whatslly/ui-components/componentsUtils";
import classnames from "classnames";
import styles from "../../message.module.scss";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import type { ComponentMessageType } from "../../../../types/ComponentMessageType";
import { findScrollableParent } from "./utils";
import type { MessageFloatingActionType } from "../FloatingActions";
import { MessageMobileHandleStore } from "../../MessageMobileHandleStore";
import { observer } from "mobx-react-lite";

const LIST_ELEMENT_PADDING_Y = 20;
const LIST_ACTION_ELEMENT_HEIGHT = 40;

export type MessageInlineActionType = {
  permission?: boolean;
  onClick: (message: ComponentMessageType) => void;
  label: string;
};

type InlineActionsFloatingListActionType = MessageInlineActionType & { isFloatingAction?: boolean };

const InlineActionsFloatingList = React.memo<{
  inlineActions: InlineActionsFloatingListActionType[] | undefined;
  onClick: () => void;
  message: ComponentMessageType;
}>(({ inlineActions, onClick, message }) =>
  inlineActions?.map((action, key) => (
    <div
      className={classnames(styles.inlineAction, { [styles.isFloatingAction]: action.isFloatingAction })}
      onClick={() => {
        action.onClick(message);
        onClick();
      }}
      key={`${action.label}-${key}`}
      data-testid={`inline-action-${message.id}-${key}`}
    >
      {action.label}
    </div>
  ))
);

export const InlineActions: React.FC<{
  message: ComponentMessageType;
  inlineActions: MessageInlineActionType[] | undefined;
  messageRowRef: React.MutableRefObject<HTMLDivElement | null>;
  floatingActions: MessageFloatingActionType[] | undefined;
  mobileStore: MessageMobileHandleStore;
  isOpen: boolean;
  setIsOpen: (newState: boolean) => void;
}> = observer(({ message, inlineActions, messageRowRef, floatingActions, mobileStore, isOpen, setIsOpen }) => {
  const [openDirections, setOpenDirections] = useState({ left: message.isSent, bottom: true });
  const arrowRef = useRef<HTMLDivElement>(null);
  const clickOutSideRef = useClickOutside<HTMLDivElement>({
    callback: () => setIsOpen(false),
    activate: true,
    ignoredElements: [arrowRef],
  });

  const inlineActionsCombinedWithFloatingActions = useMemo<InlineActionsFloatingListActionType[] | undefined>(() => {
    if (!inlineActions && !floatingActions) return undefined;
    const safetyInlineActionsAsArray = inlineActions || [];
    const safetyFloatingActionsAsArray: InlineActionsFloatingListActionType[] =
      floatingActions?.map((action) => ({ label: action.tooltip, permission: action.permission, onClick: action.onClick, isFloatingAction: true })) ||
      [];
    return [...safetyInlineActionsAsArray, ...safetyFloatingActionsAsArray];
  }, [floatingActions, inlineActions]);

  const noActions =
    !inlineActionsCombinedWithFloatingActions ||
    inlineActionsCombinedWithFloatingActions.findIndex((action) => action.permission === undefined || action.permission) === -1;

  const shouldOpenToBottom = useCallback(() => {
    const element = messageRowRef.current;
    if (!element || !isOpen || !inlineActions) return false;
    const scrollableParent = findScrollableParent(clickOutSideRef.current);
    // No need to change display style; assuming isOpen has already handled this
    const elementRect = element.getBoundingClientRect();
    const parentRect = scrollableParent?.getBoundingClientRect();
    const relativeBottom = elementRect.bottom - (parentRect?.top || 0) + (inlineActions.length * LIST_ACTION_ELEMENT_HEIGHT + LIST_ELEMENT_PADDING_Y);
    return !(scrollableParent?.clientHeight && relativeBottom > scrollableParent.clientHeight);
  }, [clickOutSideRef, inlineActions, isOpen, messageRowRef]);

  const shouldOpenToTheLeft = useCallback(() => {
    const element = messageRowRef.current?.firstChild;
    if (!element || !isOpen || message.isSent) return true;
    const scrollableParent = findScrollableParent(clickOutSideRef.current);
    const elementRect = (element as HTMLDivElement).getBoundingClientRect();
    const parentRect = scrollableParent?.getBoundingClientRect();
    const contentWidth = clickOutSideRef.current?.clientWidth;
    if (!contentWidth) return !message.isSent;
    return (parentRect?.width || 0) <= elementRect.width + contentWidth;
  }, [clickOutSideRef, isOpen, message.isSent, messageRowRef]);

  useEffect(() => {
    if (!mobileStore.userIsHoldingMouseDown) return;
    setIsOpen(true);
  }, [mobileStore.userIsHoldingMouseDown]);

  useLayoutEffect(() => {
    setOpenDirections({ left: shouldOpenToTheLeft(), bottom: shouldOpenToBottom() });
  }, [isOpen]);
  if (noActions) return <></>;
  return (
    <>
      <div
        className={classnames(styles.inlineActionsContainer, {
          [styles.inlineActionsAreOpen]: isOpen,
          [styles.openToTheLeft]: openDirections.left,
          [styles.openToBottom]: openDirections.bottom,
        })}
        ref={clickOutSideRef}
        data-testid={`inline-actions-floating-container-${message.id}`}
      >
        <InlineActionsFloatingList inlineActions={inlineActionsCombinedWithFloatingActions} onClick={() => setIsOpen(false)} message={message} />
      </div>
      <div className={classnames(styles.inlineActionsWrapper, { [styles.arrowForMessageWithTail]: message.withTail, [styles.isOpen]: isOpen })}>
        <div className={styles.inlineActionsArrow} ref={arrowRef} data-testid={`inline-actions-arrow-${message.id}`}>
          <SvgV2 iconName={"arrow_down"} height={12} width={12} onClick={() => setIsOpen(!isOpen)} />
        </div>
      </div>
    </>
  );
});
